import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Animated,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Share,
  Text,
  TextInput,
  View,
} from 'react-native';
import { Image } from 'expo-image';
import { LogOut, Pencil, Share2 } from 'lucide-react-native';
import { buildCompetitionSquadJoinUrl } from '../../../src/competitions/helpers/competitionInviteLink.helper';
import { useCompetitionId } from '../../../src/competitions/contexts/CompetitionContext';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useSnackbar } from '../../../src/shared/hooks/useSnackbar';
import { sanitizeInput } from '../../../src/helpers/sanitizeInput.helper';
import { GROUP_NAME_MAX_LENGTH, GROUP_NAME_MIN_LENGTH } from '../../../src/friends/constants/groupName';
import { getWorldCupMemberAvatarSource } from '../../../src/worldcup/helpers/worldCupMemberAvatarSource.helper';
import { LayeredAvatarView } from '../../avatars/LayeredAvatarLab/LayeredAvatarView';
import { canRenderLayeredAvatar } from '../../avatars/LayeredAvatarLab/characterCustomizer.helper';
import { getGroupCoinPayoutBreakdown } from '../../../src/worldcup/helpers/worldCupGroupsDisplay.helper';
import { IDLE_COIN_SOURCE } from '../../../constants/assets';
import { WC_GROUP_CREATED_COPY } from '../WorldCupGroupCreatedScreen/WorldCupGroupCreatedScreen.constants';
import { copyToClipboardFallback } from '../../../src/shared/helpers/clipboard.helper';
import { WorldCupCrestPreview } from '../WorldCupCrestPreview';
import { deriveGroupCrest } from '../../../src/worldcup/helpers/worldCupCrest.helper';
import type { CrestColorId, CrestEmblemId } from '../CreateWorldCupGroupScreen/CreateWorldCupGroupScreen.constants';
import { getGroupInfoSheetStyles } from './GroupInfoSheet.styles';
import {
  GROUP_INFO_SHEET_AVATAR_SIZE,
  GROUP_INFO_SHEET_COPY,
  GROUP_INFO_SHEET_MEMBERS_MAX,
  GROUP_INFO_SHEET_SLIDE_DISTANCE,
  PRIZE_MEDALS,
  buildShareInviteMessage,
  getShareInviteTitle,
} from './GroupInfoSheet.constants';

import type { WorldCupGroup, LeaderboardEntry } from '../../../src/worldcup/types/worldcup.types';

interface GroupInfoSheetProps {
  visible: boolean;
  group: WorldCupGroup;
  isSquadMember: boolean;
  /** When false, hide Leave (sole admin must hand off admin first). */
  canLeaveSquad?: boolean;
  canRenameSquad?: boolean;
  renameSquadBusy?: boolean;
  onRenameSquad?: (nextName: string) => Promise<void>;
  /** Leaderboard entries used as fallback when group.memberPreview is absent (e.g. cold cache). */
  leaderboardEntries?: LeaderboardEntry[];
  onClose: () => void;
  onLeave: () => void;
}

export const GroupInfoSheet: React.FC<GroupInfoSheetProps> = ({
  visible,
  group,
  isSquadMember,
  canLeaveSquad = true,
  canRenameSquad = false,
  renameSquadBusy = false,
  onRenameSquad,
  leaderboardEntries,
  onClose,
  onLeave,
}) => {
  const theme = useTheme();
  const competitionId = useCompetitionId();
  const styles = useMemo(() => getGroupInfoSheetStyles(theme), [theme]);
  const { showSnackbar } = useSnackbar();

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(group.name);

  const slideAnim = useRef(new Animated.Value(GROUP_INFO_SHEET_SLIDE_DISTANCE)).current;

  useEffect(() => {
    if (visible) {
      Animated.spring(slideAnim, {
        toValue: 0,
        damping: 22,
        stiffness: 200,
        useNativeDriver: true,
      }).start();
    } else {
      setIsEditingName(false);
    }
  }, [visible, slideAnim]);

  useEffect(() => {
    if (!isEditingName) {
      setNameDraft(group.name);
    }
  }, [group.name, isEditingName]);

  const handleNameChange = useCallback((text: string) => {
    const sanitized = sanitizeInput(text, true);
    setNameDraft(sanitized.slice(0, GROUP_NAME_MAX_LENGTH));
  }, []);

  const handleCancelNameEdit = useCallback(() => {
    setNameDraft(group.name);
    setIsEditingName(false);
  }, [group.name]);

  const handleSaveNameEdit = useCallback(async () => {
    if (!onRenameSquad) return;
    const trimmed = nameDraft.trim();
    if (trimmed.length < GROUP_NAME_MIN_LENGTH) {
      showSnackbar(`Squad name must be at least ${GROUP_NAME_MIN_LENGTH} characters`, 'error');
      return;
    }
    if (trimmed === group.name.trim()) {
      setIsEditingName(false);
      return;
    }
    try {
      await onRenameSquad(trimmed);
      setIsEditingName(false);
    } catch {
      /* errors surfaced by parent mutation */
    }
  }, [group.name, nameDraft, onRenameSquad, showSnackbar]);

  const handleClose = useCallback(() => {
    Animated.timing(slideAnim, {
      toValue: GROUP_INFO_SHEET_SLIDE_DISTANCE,
      duration: 210,
      useNativeDriver: true,
    }).start(() => onClose());
  }, [onClose, slideAnim]);

  const inviteCode =
    group.inviteCode != null && String(group.inviteCode).trim().length > 0
      ? String(group.inviteCode).trim()
      : null;

  const handleCopyCode = useCallback(async () => {
    if (!inviteCode) return;
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(inviteCode);
        } else {
          copyToClipboardFallback(inviteCode);
        }
      } else {
        await Share.share({ message: inviteCode });
      }
      showSnackbar(WC_GROUP_CREATED_COPY.CODE_COPIED, 'success');
    } catch {
      /* user cancelled */
    }
  }, [inviteCode, showSnackbar]);

  const handleShareInvite = useCallback(async () => {
    if (!inviteCode) return;
    const inviteLink = buildCompetitionSquadJoinUrl(competitionId, inviteCode, 'b2b');
    if (!inviteLink) return;
    const message = buildShareInviteMessage(group.name, inviteLink);
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({ title: getShareInviteTitle(), text: message });
      } else {
        await Share.share({ message });
      }
    } catch {
      /* user cancelled */
    }
  }, [inviteCode, group.name]);

  const memberItems = useMemo(() => {
    const previews = group.memberPreview ?? [];
    if (previews.length > 0) {
      return previews.slice(0, GROUP_INFO_SHEET_MEMBERS_MAX).map((m) => ({
        source: getWorldCupMemberAvatarSource(m.avatarUrl, m.avatarId),
        avatarConfig: m.avatarConfig ?? null,
        label: m.displayName,
      }));
    }
    const entries = leaderboardEntries ?? [];
    return entries.slice(0, GROUP_INFO_SHEET_MEMBERS_MAX).map((e) => ({
      source: getWorldCupMemberAvatarSource(e.avatarUrl ?? null, e.avatarId ?? null),
      avatarConfig: e.avatarConfig ?? null,
      label: e.name,
    }));
  }, [group.memberPreview, leaderboardEntries]);

  const { colorId: crestColorId, emblemId: crestEmblemId } = useMemo(() => {
    const derived = deriveGroupCrest(group.id, group.name);
    return {
      colorId: (group.crestColor as CrestColorId | null | undefined) ?? derived.colorId,
      emblemId: (group.crestEmblem as CrestEmblemId | null | undefined) ?? derived.emblemId,
    };
  }, [group.id, group.name, group.crestColor, group.crestEmblem]);

  const rewards = group.companyBranding?.rewards ?? null;
  const description = group.description?.trim() ?? null;
  const isCompany = group.companyBranding != null;
  const coinPayouts = getGroupCoinPayoutBreakdown(
    group.entryFeeCoins ?? 0,
    group.memberCount,
    group.payoutMode,
    group.payoutFirstPct,
    group.payoutSecondPct,
    group.payoutThirdPct,
  );

  const memberLabel = isCompany
    ? GROUP_INFO_SHEET_COPY.MEMBER_LABEL_COMPANY
    : GROUP_INFO_SHEET_COPY.MEMBER_LABEL_COMMUNITY;
  const coinFormatter = useMemo(() => new Intl.NumberFormat(), []);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={handleClose}
      statusBarTranslucent
    >
      <View style={styles.root}>
        <Pressable style={styles.backdrop} onPress={handleClose} accessibilityRole="button" />
        <Animated.View style={[styles.sheet, { transform: [{ translateY: slideAnim }] }]}>
          <View style={styles.handle} />

          {/* Header */}
          <View style={styles.header}>
            <View style={[styles.headerBadge, isCompany && styles.headerBadgeLogo]}>
              {isCompany && group.companyBranding?.logoUrl != null ? (
                <Image
                  source={{ uri: group.companyBranding.logoUrl as string }}
                  style={styles.headerLogoImage}
                  contentFit="contain"
                  alt={group.companyBranding.companyName ?? group.name}
                />
              ) : (
                <WorldCupCrestPreview
                  colorId={crestColorId}
                  emblemId={crestEmblemId}
                  name={group.name}
                  size={46}
                />
              )}
            </View>
            <View style={styles.headerInfo}>
              {canRenameSquad && isEditingName ? (
                <>
                  <TextInput
                    value={nameDraft}
                    onChangeText={handleNameChange}
                    style={styles.headerNameInput}
                    maxLength={GROUP_NAME_MAX_LENGTH}
                    editable={!renameSquadBusy}
                    autoFocus
                    selectTextOnFocus
                    accessibilityLabel={GROUP_INFO_SHEET_COPY.EDIT_SQUAD_NAME_A11Y}
                  />
                  <View style={styles.headerEditActions}>
                    <Pressable
                      onPress={() => { void handleSaveNameEdit(); }}
                      disabled={renameSquadBusy}
                      accessibilityRole="button"
                      style={({ pressed, hovered }) => [
                        styles.headerEditActionBtn,
                        styles.headerEditActionBtnPrimary,
                        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                        pressed && !renameSquadBusy && styles.headerEditActionBtnPressed,
                        hovered && Platform.OS === 'web' && !renameSquadBusy && styles.headerEditActionBtnHover,
                        renameSquadBusy && styles.headerEditActionBtnDisabled,
                      ]}
                    >
                      <Text style={[styles.headerEditActionBtnText, styles.headerEditActionBtnTextPrimary]}>
                        {GROUP_INFO_SHEET_COPY.SAVE_SQUAD_NAME}
                      </Text>
                    </Pressable>
                    <Pressable
                      onPress={handleCancelNameEdit}
                      disabled={renameSquadBusy}
                      accessibilityRole="button"
                      style={({ pressed, hovered }) => [
                        styles.headerEditActionBtn,
                        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                        pressed && !renameSquadBusy && styles.headerEditActionBtnPressed,
                        hovered && Platform.OS === 'web' && !renameSquadBusy && styles.headerEditActionBtnHover,
                        renameSquadBusy && styles.headerEditActionBtnDisabled,
                      ]}
                    >
                      <Text style={styles.headerEditActionBtnText}>
                        {GROUP_INFO_SHEET_COPY.CANCEL_RENAME}
                      </Text>
                    </Pressable>
                  </View>
                </>
              ) : (
                <View style={canRenameSquad ? styles.headerNameRow : undefined}>
                  <Text style={styles.headerName} numberOfLines={1}>{group.name}</Text>
                  {canRenameSquad && (
                    <Pressable
                      onPress={() => setIsEditingName(true)}
                      hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                      accessibilityRole="button"
                      accessibilityLabel={GROUP_INFO_SHEET_COPY.EDIT_SQUAD_NAME_A11Y}
                      style={({ pressed, hovered }) => [
                        styles.headerEditPencilBtn,
                        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                        pressed && styles.headerEditPencilBtnPressed,
                        hovered && Platform.OS === 'web' && styles.headerEditPencilBtnHover,
                      ]}
                    >
                      <Pencil size={13} color={theme.gray[500]} strokeWidth={2.2} />
                    </Pressable>
                  )}
                </View>
              )}
              <Text style={styles.headerMeta}>
                {group.memberCount} {memberLabel}
              </Text>
              {description != null && description.length > 0 && (
                <Text style={styles.headerDescription} numberOfLines={2}>{description}</Text>
              )}
            </View>
          </View>

          <ScrollView
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.scrollContent}
          >
            {/* Members */}
            {memberItems.length > 0 && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>{GROUP_INFO_SHEET_COPY.MEMBERS_SECTION}</Text>
                </View>
                <View style={styles.membersRow}>
                  {memberItems.map((m, i) => (
                    <View key={`member-${i}-${m.label}`} style={styles.memberItem}>
                      {/*
                        A custom look wins over the ready-avatar image, guarded on
                        `canRenderLayeredAvatar` so an app that predates a newly-added base
                        falls back to the image rather than drawing a hole.
                      */}
                      {m.avatarConfig && canRenderLayeredAvatar(m.avatarConfig) ? (
                        <LayeredAvatarView
                          equipped={m.avatarConfig}
                          size={GROUP_INFO_SHEET_AVATAR_SIZE}
                        />
                      ) : m.source != null ? (
                        <Image
                          source={m.source}
                          style={styles.memberAvatar}
                          contentFit="contain"
                          alt=""
                        />
                      ) : null}
                      <Text style={styles.memberName} numberOfLines={2}>{m.label}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Prize pool */}
            {coinPayouts.length > 0 && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>{GROUP_INFO_SHEET_COPY.PRIZE_POOL_SECTION}</Text>
                </View>
                {coinPayouts.map(({ place, coins }) => (
                  <View key={place} style={styles.prizePoolRow}>
                    <Text style={styles.prizeMedal}>{PRIZE_MEDALS[place] ?? '🏅'}</Text>
                    <Text style={[
                      styles.prizePoolPlaceLabel,
                      place === 1 ? styles.prizePoolPlaceLabelGold
                        : place === 2 ? styles.prizePoolPlaceLabelSilver
                        : styles.prizePoolPlaceLabelBronze,
                    ]}>
                      {GROUP_INFO_SHEET_COPY.PLACE_LABELS[place] ?? `#${place}`}
                    </Text>
                    <View style={styles.prizePoolCoinRow}>
                      <Image source={IDLE_COIN_SOURCE} style={styles.prizePoolCoin} contentFit="contain" alt="" />
                      <Text style={styles.prizePoolAmount}>{coinFormatter.format(coins)}</Text>
                    </View>
                  </View>
                ))}
              </View>
            )}

            {/* Group code */}
            {inviteCode != null && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>{GROUP_INFO_SHEET_COPY.INVITE_SECTION}</Text>
                </View>
                <View style={styles.inviteBox}>
                  <Text style={styles.inviteCodeLabel}>{GROUP_INFO_SHEET_COPY.INVITE_SECTION}</Text>
                  <Text style={styles.inviteCode} selectable>{inviteCode}</Text>
                  <View style={styles.inviteActions}>
                    <Pressable
                      style={({ pressed, hovered }) => [
                        styles.inviteActionBtn,
                        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                        pressed && styles.inviteActionBtnPressed,
                        hovered && Platform.OS === 'web' && styles.inviteActionBtnHover,
                      ]}
                      onPress={() => { void handleCopyCode(); }}
                      accessibilityRole="button"
                    >
                      <Text style={styles.inviteActionBtnText}>{GROUP_INFO_SHEET_COPY.COPY_CODE}</Text>
                    </Pressable>
                    <Pressable
                      style={({ pressed, hovered }) => [
                        styles.inviteActionBtn,
                        styles.inviteActionBtnPrimary,
                        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                        pressed && styles.inviteActionBtnPressed,
                        hovered && Platform.OS === 'web' && styles.inviteActionBtnHover,
                      ]}
                      onPress={() => { void handleShareInvite(); }}
                      accessibilityRole="button"
                    >
                      <Share2 size={14} color={theme.onPrimary} strokeWidth={2.2} />
                      <Text style={[styles.inviteActionBtnText, styles.inviteActionBtnTextPrimary]}>
                        {GROUP_INFO_SHEET_COPY.SHARE_CODE}
                      </Text>
                    </Pressable>
                  </View>
                </View>
              </View>
            )}

            {/* Prizes */}
            {rewards != null && rewards.length > 0 && (
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>{GROUP_INFO_SHEET_COPY.PRIZES_SECTION}</Text>
                </View>
                {rewards.map((reward) => (
                  <View
                    key={reward.place}
                    style={[
                      styles.prizeRow,
                      reward.place === 1 ? styles.prizeRowBorderGold
                        : reward.place === 2 ? styles.prizeRowBorderSilver
                        : styles.prizeRowBorderBronze,
                    ]}
                  >
                    <Text style={styles.prizeMedal}>{PRIZE_MEDALS[reward.place] ?? '🏅'}</Text>
                    <View style={styles.prizeInfo}>
                      <Text style={[
                        styles.prizePlaceLabel,
                        reward.place === 1 ? styles.prizePlaceLabelGold
                          : reward.place === 2 ? styles.prizePlaceLabelSilver
                          : styles.prizePlaceLabelBronze,
                      ]}>
                        {GROUP_INFO_SHEET_COPY.PLACE_LABELS[reward.place] ?? `#${reward.place}`}
                      </Text>
                      <Text style={styles.prizeDescription}>{reward.label}</Text>
                    </View>
                  </View>
                ))}
              </View>
            )}

            {/* Leave */}
            {isSquadMember && !isCompany && (
              <View style={styles.dangerZone}>
                {canLeaveSquad ? (
                  <Pressable
                    style={({ pressed, hovered }) => [
                      styles.leaveButton,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && styles.leaveButtonPressed,
                      hovered && Platform.OS === 'web' && styles.leaveButtonHover,
                    ]}
                    onPress={onLeave}
                    accessibilityRole="button"
                    accessibilityLabel={GROUP_INFO_SHEET_COPY.LEAVE_SQUAD}
                  >
                    <LogOut size={16} color={theme.error} />
                    <Text style={styles.leaveButtonLabel}>{GROUP_INFO_SHEET_COPY.LEAVE_SQUAD}</Text>
                  </Pressable>
                ) : null}
                <Text style={styles.leaveNote}>
                  {canLeaveSquad
                    ? GROUP_INFO_SHEET_COPY.LEAVE_SQUAD_NOTE
                    : GROUP_INFO_SHEET_COPY.SOLE_ADMIN_LEAVE_NOTE}
                </Text>
              </View>
            )}
          </ScrollView>
        </Animated.View>
      </View>
    </Modal>
  );
};
